import React, { useEffect, useState } from 'react';
import { ChatInput as Input } from '@zurich-zurichat/chat-input';
import {
  Chip,
  ChipsGroup,
  FeedbackConfirmation,
  LoadingSpinner
} from 'app/components';
import {
  useAppSelector,
  useAppDispatch,
  setService,
  getService,
  setFeedbackConfirmation,
  setEmailChain,
  getBusinessArea,
  getBootstrapApp,
  setCoverageCheckV2Id,
  getUserBusinessUnits,
  setSelectedBusinessUnit,
  getSelectedBusinessUnit
} from 'app/redux';
import { sendFeedback } from 'integration';
import { smoothScrollingToAnchor } from 'app/utils';
import styles from './Services.module.scss';

import config from 'config';
import { SERVICE_NAMES } from 'app/utils/consts';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as AccessDoorIcon } from 'app/assets/Icons/Chips/AccesDoor.svg';
import { useNavigate } from 'react-router-dom';
import { getDocGeneratorLoginCode } from 'integration/cross-functional/doc-generator/api';

export const Services: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const bootstrap = useAppSelector(getBootstrapApp);
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const [scrollTrigger, setScrollTrigger] = useState<boolean>(false);
  const userBusinessUnits = useAppSelector(getUserBusinessUnits);
  const selectedBusinessUnit = useAppSelector(getSelectedBusinessUnit);

  const services = bootstrap.services;
  const isIGPR = currentService === SERVICE_NAMES.IGPR_SCORE;
  const isCoverageCheckV2 = currentService === SERVICE_NAMES.COVERAGE_CHECK_V2;
  const isAYD = currentService === SERVICE_NAMES.ASK_YOUR_DOCS;
  const [businessUnitSelector, setBusinessUnitSelector] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const defaultBusinessUnit = {
    businessUnitName: 'Default',
    businessUnit: 'default'
  };

  useEffect(() => {
    const hasActiveServices = Object.values(services).some(
      (service) => service.active
    );

    if (hasActiveServices) {
      setIsLoading(false);
    }
  }, [services]);

  const handleServiceSelection = ({
    status,
    value
  }: {
    status: boolean;
    value: string;
  }): void => {
    dispatch(setService(value));
    dispatch(setSelectedBusinessUnit('default'));
    setBusinessUnitSelector(false);

    if (value === SERVICE_NAMES.NDA_GENERATOR && status) {
      navigate('/app/nda-generator');
      return;
    }

    if (value === SERVICE_NAMES.DOC_GENERATOR && status) {
      getDocGeneratorLoginCode()
        .then((code) => {
          window.location.href = `${config.APP.DOC_GENERATOR_URL}/login?code=${code}`;
        })
        .catch(() => {
          window.location.href = config.APP.DOC_GENERATOR_URL;
        });
      return;
    }

    if (userBusinessUnits) {
      const businessUnits = userBusinessUnits[value];

      if (businessUnits) {
        if (businessUnits.length === 1) {
          dispatch(setSelectedBusinessUnit(businessUnits[0].businessUnit));
        } else if (businessUnits.length > 1) {
          setBusinessUnitSelector(true);
        }
      }
    }
    if (currentService === value && !status) {
      dispatch(setService(undefined));
      dispatch(setEmailChain(undefined));
    }
    if (status) setScrollTrigger(true);
  };

  const handleNavigateToSummary = (): void => {
    navigate(config.APP.PATH.SUMMARY);
  };

  const renderIGPRServicesDirectAccess = (): React.JSX.Element => (
    <div>
      <Button
        variant={'secondary'}
        onClick={handleNavigateToSummary}
        className={styles.servicesDirectAccessButton}
      >
        <Button.Icon>
          <AccessDoorIcon />
        </Button.Icon>
        <Button.Label>Go to iGPR AI Model tool</Button.Label>
      </Button>
    </div>
  );

  const handleSubmiFeedback = (feedback: string): void => {
    sendFeedback({
      origin: 'something-is-missing-business-services',
      feedback,
      businessArea: currentBusinessArea,
      businessService: currentService
    }).then(dispatch(setFeedbackConfirmation(true)));
  };

  const handleGoToCoverageCheckToolClick = (): void => {
    dispatch(setCoverageCheckV2Id(undefined));
    navigate(config.APP.PATH.COVERAGE_CHECK_V2);
  };

  const renderCoverageCheckTool = (): React.JSX.Element => (
    <div>
      <Button
        variant={'secondary'}
        onClick={handleGoToCoverageCheckToolClick}
        className={styles.servicesDirectAccessButton}
      >
        <Button.Icon>
          <AccessDoorIcon />
        </Button.Icon>
        <Button.Label>Go to the tool</Button.Label>
      </Button>
    </div>
  );

  const renderLoadingSpinner = (): React.JSX.Element => (
    <LoadingSpinner
      containerStyle={{
        position: 'relative',
        left: '1.8rem',
        width: '0rem',
        height: '0rem',
        backdropFilter: 'unset',
        paddingBottom: '1.4375rem'
      }}
      imageStyle={{ width: '6rem', height: '2rem', left: '2rem' }}
    />
  );

  const renderAYDServicesDirectAccess = (): React.JSX.Element => (
    <div>
      <Button
        variant={'secondary'}
        onClick={handleNavigateToSummary}
        className={styles.servicesDirectAccessButton}
      >
        <Button.Icon>
          <AccessDoorIcon />
        </Button.Icon>
        <Button.Label>Go to Ask Your Docs</Button.Label>
      </Button>
    </div>
  );

  useEffect(() => {
    if (scrollTrigger) {
      if (businessUnitSelector) {
        smoothScrollingToAnchor('businessUnitSelector');
      } else if (isCoverageCheckV2) {
        smoothScrollingToAnchor('coverageCheckV2');
      } else if (isIGPR) {
        smoothScrollingToAnchor('iGPRServicesDirectAccess');
      } else if (isAYD) {
        smoothScrollingToAnchor('AYDTool');
      } else {
        smoothScrollingToAnchor('flow');
      }
    }
    setScrollTrigger(false);
  }, [scrollTrigger]);

  useEffect(() => {
    setBusinessUnitSelector(false);
    if (userBusinessUnits && currentService) {
      const businessUnits = userBusinessUnits[currentService];

      if (businessUnits) {
        if (businessUnits.length === 1) {
          dispatch(setSelectedBusinessUnit(businessUnits[0].businessUnit));
        } else if (businessUnits.length > 1) {
          setBusinessUnitSelector(true);
        }
      }
    }
  }, [currentBusinessArea, currentService]);

  return (
    <div className={styles.services} id="services">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h3 className={styles['heading-3']}>Discover all services</h3>
        {isLoading ? renderLoadingSpinner() : null}
      </div>
      <p>
        Welcome to the GenAI Service overview! Here, you&apos;ll find a
        comprehensive overview of our current and upcoming services. Our list of
        implemented and planned services is continuously expanding as we strive
        to meet your evolving needs. We value your input, so if you have any new
        requests or suggestions, please feel free to share them in the feedback
        section below. We&apos;re here to make your AI experience exceptional.
      </p>
      <div className={styles.chips}>
        {
          <ChipsGroup
            chipElements={services}
            selected={currentService}
            serviceMode
            onClick={handleServiceSelection}
          />
        }
      </div>
      <div className={styles.maturity}>
        <span
          style={{ backgroundColor: 'var(--color-gradiant-blue-20)' }}
          aria-hidden="true"
        />
        <span
          style={{ backgroundColor: 'var(--color-gradiant-blue-40)' }}
          aria-hidden="true"
        />
        <span
          style={{ backgroundColor: 'var(--color-gradiant-blue-60)' }}
          aria-hidden="true"
        />
        <span className={styles.maturityLabel}>Maturity level</span>
      </div>
      <p className={styles.servicesDirectAccessFeedbackText}>
        Our list of implemented and planned services is continuously expanding
        as we strive to meet your evolving needs. We value your input, so if you
        have any new requests or suggestions, please feel free to share them in
        the feedback section below. We&apos;re here to make your AI experience
        exceptional.
      </p>
      <Input
        className={styles.input}
        inputProps={{ placeholder: 'Something is missing' }}
        onSubmit={(feedback) => {
          handleSubmiFeedback(feedback);
        }}
      />
      <FeedbackConfirmation />
      {businessUnitSelector && (
        <div
          className={styles.servicesDirectAccessContainer}
          id="businessUnitSelector"
        >
          <h3 className={styles.servicesDirectAccessTitle}>
            Select the Business Unit you want to use
          </h3>
          <p className={styles.servicesDirectAccessText}>
            You have assigned the following business units, select the one want
            to use for service.
          </p>
          <div className={styles.businessUnitsChips}>
            {userBusinessUnits &&
              currentService &&
              [defaultBusinessUnit, ...userBusinessUnits[currentService]].map(
                ({ businessUnit, businessUnitName: name }) => (
                  <Chip
                    key={`chip-${businessUnit}`}
                    id={businessUnit}
                    value={name}
                    selected={businessUnit === selectedBusinessUnit}
                    onClick={() => {
                      dispatch(setSelectedBusinessUnit(businessUnit));
                      setScrollTrigger(true);
                    }}
                  />
                )
              )}
          </div>
        </div>
      )}
      {isIGPR && (
        <div
          className={styles.servicesDirectAccessContainer}
          id="iGPRServicesDirectAccess"
        >
          <h3 className={styles.servicesDirectAccessTitle}>
            iGPR AI Model <span style={{ color: '#0F67BF' }}>Tool</span>
          </h3>
          <p className={styles.servicesDirectAccessText}>
            Welcome to the GenAI Service overview! Here, you&apos;ll find a
            comprehensive overview of our current and upcoming services. Please
            select a service from the list below.
          </p>
          <div style={{ placeItems: 'center', marginTop: '32px' }}>
            {renderIGPRServicesDirectAccess()}
          </div>
        </div>
      )}
      {isCoverageCheckV2 && (
        <div
          className={styles.servicesDirectAccessContainer}
          id="coverageCheckV2"
        >
          <h3 className={styles.servicesDirectAccessTitle}>
            Coverage Check <span style={{ color: '#0F67BF' }}>Tool</span>
          </h3>
          <p className={styles.servicesDirectAccessText}>
            The Coverage Check tool helps you analyze documents and determine
            coverage status based on policy terms. Please click the button below
            to use it.
          </p>
          <div style={{ placeItems: 'center', marginTop: '32px' }}>
            {renderCoverageCheckTool()}
          </div>
        </div>
      )}
      {isAYD && (
        <div className={styles.servicesDirectAccessContainer} id="AYDTool">
          <h3 className={styles.servicesDirectAccessTitle}>
            Ask Your Docs <span style={{ color: '#0F67BF' }}>Tool</span>
          </h3>

          <p className={styles.servicesDirectAccessText}>
            The Ask Your Docs tool lets you easily manage and analyze your
            documents using AI. Click the button below to get started with
            uploading and exploring your documents.
          </p>

          <div style={{ placeItems: 'center', marginTop: '32px' }}>
            {renderAYDServicesDirectAccess()}
          </div>
        </div>
      )}
    </div>
  );
};
